'use client';

import { useState, useEffect } from 'react';
import { Settings, X, ChevronDown, ChevronUp } from 'lucide-react';
import { useTenantAndVariant } from '../multitenancy/TenantAndVariantContext';


export function DebugPanel() {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    layout: true,
    styling: false,
    features: false,
  });

  const {
    heroVariant,
    setHeroVariant,
    headerVariant,
    setHeaderVariant,
    footerVariant,
    setFooterVariant,
    featureToggles,
    setFeatureToggles,
  } = useTenantAndVariant();

  // Keyboard shortcut: Ctrl+Shift+D
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  const handleVariantChange = (controlId: string, value: string) => {
    if (controlId === 'productCardAnimation') {
      setFeatureToggles({ ...featureToggles, productCardAnimation: value as 'fade' | 'zoom' | 'slide-up' | 'glow' });
    } else if (controlId === 'ctaButtonStyle') {
      setFeatureToggles({ ...featureToggles, ctaButtonStyle: value as 'bold' | 'outline' | 'gradient' | 'animated' });
    } else if (controlId === 'promoStyle') {
      setFeatureToggles({ ...featureToggles, promoStyle: value as 'static' | 'animated' | 'countdown' | 'urgency' });
    } else if (controlId === 'gridColumns') {
      setFeatureToggles({ ...featureToggles, gridColumns: value === 'masonry' ? 'masonry' : value === 'metro' ? 'metro' : parseInt(value) as 2 | 3 | 4 });
    } else if (controlId === 'influencerLayout') {
      setFeatureToggles({ ...featureToggles, influencerLayout: value as 'grid' | 'carousel' | 'spotlight' });
    } else if (controlId === 'newsletterStyle') {
      setFeatureToggles({ ...featureToggles, newsletterStyle: value as 'inline' | 'emphasis' | 'minimal' });
    } else if (controlId === 'categoryTextStyle') {
      setFeatureToggles({ ...featureToggles, categoryTextStyle: value as 'normal' | 'bold' });
    } else if (controlId === 'categoryGridLayout') {
      setFeatureToggles({ ...featureToggles, categoryGridLayout: value as 'hidden' | 'tiles' | 'contained' });
    } else if (controlId === 'heroWidthMode') {
      setFeatureToggles({ ...featureToggles, heroWidthMode: value as 'full' | 'limited' | 'focused' });
    }
  };

  const handleFeatureToggle = (feature: keyof typeof featureToggles) => {
    setFeatureToggles(prev => ({
      ...prev,
      [feature]: !prev[feature]
    }));
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Only show in development
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-[9999] bg-blue-600 hover:bg-blue-700 text-white rounded-full p-4 shadow-lg transition-all hover:scale-110"
        aria-label="Toggle Debug Panel"
        title="Debug Settings (Ctrl+Shift+D)"
      >
        <Settings size={24} className={isOpen ? 'rotate-90' : ''} />
      </button>

      {/* Debug Panel */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-[9999] bg-white border-2 border-blue-600 rounded-lg shadow-2xl w-96 max-h-[calc(100vh-140px)] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white z-10 flex items-center justify-between p-4 border-b border-gray-200">
            <div>
              <h3 className="text-lg font-bold text-gray-900">A/B Testing Panel</h3>
              <p className="text-xs text-gray-500 mt-0.5">Ctrl+Shift+D to toggle</p>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-500 hover:text-gray-700 transition-colors"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>

          <div className="p-4 space-y-4">
            {/* Layout Variants Section */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection('layout')}
                className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <span className="font-semibold text-gray-900">Layout Variants</span>
                {expandedSections.layout ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </button>

              {expandedSections.layout && (
                <div className="p-3 space-y-3">
                  {/* Header Variant */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Header (Current: {headerVariant})
                    </label>
                    <select
                      value={headerVariant}
                      onChange={(e) => {
                        console.log('Changing header variant to:', e.target.value);
                        setHeaderVariant(e.target.value);
                      }}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="showcase">Showcase - Centered Logo</option>
                      <option value="classic">Classic - Left Logo Inline</option>
                    </select>
                  </div>

                  {/* Hero Variant */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Hero</label>
                    <select
                      value={heroVariant}
                      onChange={(e) => setHeroVariant(e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="classic">Classic - Classic Elegance</option>
                      <option value="showcase">Showcase - Modern Split</option>
                      <option value="minimal">Minimal - Special Sale</option>
                    </select>
                  </div>

                  {/* Hero Width Mode */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Hero Width</label>
                    <select
                      value={featureToggles.heroWidthMode}
                      onChange={(e) => handleVariantChange('heroWidthMode', e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="full">🖥️ Full Width</option>
                      <option value="limited">📱 Limited (1920px)</option>
                      <option value="focused">🎯 Focused w/ Blur</option>
                    </select>
                  </div>

                  {/* Footer Variant */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Footer</label>
                    <select
                      value={footerVariant}
                      onChange={(e) => setFooterVariant(e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="classic">Classic - Simple</option>
                      <option value="showcase">Showcase - Multi-Column</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Styling Options Section */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection('styling')}
                className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <span className="font-semibold text-gray-900">Styling Options</span>
                {expandedSections.styling ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </button>

              {expandedSections.styling && (
                <div className="p-3 space-y-3">
                  {/* Product Card Animation */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Product Hover</label>
                    <select
                      value={featureToggles.productCardAnimation}
                      onChange={(e) => handleVariantChange('productCardAnimation', e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="zoom">🔍 Zoom</option>
                      <option value="fade">✨ Fade</option>
                      <option value="slide-up">⬆️ Slide Up</option>
                      <option value="glow">💫 Glow</option>
                    </select>
                  </div>

                  {/* CTA Button Style */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">CTA Style</label>
                    <select
                      value={featureToggles.ctaButtonStyle}
                      onChange={(e) => handleVariantChange('ctaButtonStyle', e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="bold">🎯 Bold</option>
                      <option value="outline">⭕ Outline</option>
                      <option value="gradient">🌈 Gradient</option>
                      <option value="animated">✨ Animated</option>
                    </select>
                  </div>

                  {/* Promo Style */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Promo Style</label>
                    <select
                      value={featureToggles.promoStyle}
                      onChange={(e) => handleVariantChange('promoStyle', e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="static">📄 Static</option>
                      <option value="animated">🎨 Animated</option>
                      <option value="countdown">⏰ Countdown</option>
                      <option value="urgency">🔥 Urgency</option>
                    </select>
                  </div>

                  {/* Grid Columns */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Grid Layout</label>
                    <select
                      value={featureToggles.gridColumns.toString()}
                      onChange={(e) => handleVariantChange('gridColumns', e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="2">2 Columns</option>
                      <option value="3">3 Columns</option>
                      <option value="4">4 Columns</option>
                      <option value="masonry">🧱 Masonry</option>
                      <option value="metro">🪟 Metro</option>
                    </select>
                  </div>

                  {/* Category Grid Layout */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category Grid</label>
                    <select
                      value={featureToggles.categoryGridLayout}
                      onChange={(e) => handleVariantChange('categoryGridLayout', e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="hidden">❌ Hidden</option>
                      <option value="tiles">🎨 Tiles</option>
                      <option value="contained">📦 Contained</option>
                    </select>
                  </div>

                  {/* Category Text Style */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category Text</label>
                    <select
                      value={featureToggles.categoryTextStyle}
                      onChange={(e) => handleVariantChange('categoryTextStyle', e.target.value)}
                      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="normal">Normal</option>
                      <option value="bold">Bold</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Feature Toggles Section */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection('features')}
                className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <span className="font-semibold text-gray-900">Feature Toggles</span>
                {expandedSections.features ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </button>

              {expandedSections.features && (
                <div className="p-3 space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={featureToggles.autoRotateHero}
                      onChange={() => handleFeatureToggle('autoRotateHero')}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-700">Auto-Rotate Hero 🔄</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={featureToggles.scrollableCollections}
                      onChange={() => handleFeatureToggle('scrollableCollections')}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-700">Scrollable Collections 📜</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={featureToggles.showTrustBadges}
                      onChange={() => handleFeatureToggle('showTrustBadges')}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-700">Trust Badges 🛡️</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={featureToggles.showProductReviews}
                      onChange={() => handleFeatureToggle('showProductReviews')}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-700">Product Reviews ⭐</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={featureToggles.showRecentPurchases}
                      onChange={() => handleFeatureToggle('showRecentPurchases')}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-700">Recent Purchases 🔔</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={featureToggles.showAnnouncementBar1}
                      onChange={() => handleFeatureToggle('showAnnouncementBar1')}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-700">Announcement Bar 1 📢</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={featureToggles.showAnnouncementBar2}
                      onChange={() => handleFeatureToggle('showAnnouncementBar2')}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-700">Announcement Bar 2 🎉</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={featureToggles.showAnnouncementBar3}
                      onChange={() => handleFeatureToggle('showAnnouncementBar3')}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-700">Announcement Bar 3 📦</span>
                  </label>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
